import React from "react";
import AlertCard from "./AlertCard";
import LoadingSpinner from "../common/LoadingSpinner";
import "../css/Common.css";
import "./css/AlertsIntelligenceHub.css";

/**
 * List component for displaying alerts with pagination.
 *
 * @param {Array} alerts - Array of alert objects
 * @param {boolean} loading - Loading state
 * @param {Object} pagination - Pagination info
 * @param {Function} onViewAlert - Handler for viewing an alert
 * @param {Function} onMarkReviewed - Handler for marking reviewed
 * @param {Function} onShare - Handler for sharing
 * @param {Function} onDismiss - Handler for dismissing
 * @param {Function} onLoadMore - Handler for loading more
 * @param {string} emptyMessage - Message when list is empty
 */
function AlertsList({
  alerts = [],
  loading = false,
  pagination = {},
  onViewAlert,
  onMarkReviewed,
  onShare,
  onDismiss,
  onLoadMore,
  emptyMessage = "No alerts found.",
}) {
  // ═══════════════════════════════════════════════════════════════════════════
  // LOADING STATE
  // ═══════════════════════════════════════════════════════════════════════════

  if (loading && alerts.length === 0) {
    return (
      <div className="aih-list">
        <LoadingSpinner message="Loading alerts..." />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EMPTY STATE
  // ═══════════════════════════════════════════════════════════════════════════

  if (!loading && alerts.length === 0) {
    return (
      <div className="aih-list">
        <div className="aih-list-empty">
          <span className="aih-empty-icon">—</span>
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="aih-list">
      {/* Results count */}
      {pagination.totalElements > 0 && (
        <div className="aih-list-header">
          <span className="aih-list-count">
            Showing {alerts.length} of {pagination.totalElements} alert
            {pagination.totalElements !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* Alert cards */}
      <div className="aih-list-items">
        {alerts.map((alert) => (
          <AlertCard
            key={alert.id}
            alert={alert}
            onView={onViewAlert}
            onMarkReviewed={onMarkReviewed}
            onShare={onShare}
            onDismiss={onDismiss}
          />
        ))}
      </div>

      {/* Loading more indicator */}
      {loading && alerts.length > 0 && (
        <div className="aih-list-loading-more">
          <LoadingSpinner message="Loading more..." size="sm" />
        </div>
      )}

      {/* Load more button */}
      {pagination.hasMore && !loading && (
        <div className="aih-list-footer">
          <button className="aih-load-more-btn" onClick={onLoadMore}>
            Load More
            <span className="aih-load-more-count">
              ({alerts.length} / {pagination.totalElements})
            </span>
          </button>
        </div>
      )}

      {/* End of list indicator */}
      {!pagination.hasMore && alerts.length > 0 && (
        <div className="aih-list-end">
          <span>— End of alerts —</span>
        </div>
      )}
    </div>
  );
}

export default AlertsList;
